import { useCallback, useEffect, useRef, useState } from "react";

import {
	Alert,
	Box,
	Button,
	Group,
	List,
	Modal,
	Paper,
	Stack,
	Text,
	ThemeIcon,
} from "@mantine/core";
import {
	IconAlertTriangle,
	IconCheck,
	IconMicrophone,
	IconPhoneCall,
	IconPlayerPlay,
	IconPlayerStop,
	IconSettings,
} from "@tabler/icons-react";

import { useLogger } from "@/logging/client/LoggerContext";
import {
	type AudioLevelMonitor,
	createAudioLevelMonitor,
	isRecordingSilent,
} from "../utils/audio-level-monitor";
import { caregiverAppVerbalConfirmationDisclaimer } from "./constants";
import { IvrInstructionsModal } from "../../components/ivr-instructions-modal";

export type VerbalConfirmationRecording = {
	blob: Blob;
	durationSeconds: number;
};

const MIN_DURATION_SECONDS = 2;
const MAX_DURATION_SECONDS = 60;
const UNSUPPORTED_RECORDING_ERROR =
	"Audio recording is not supported. You can check out by phone instead.";

type ModalStep = "prepare" | "prompt" | "recording" | "review";

export function VerbalConfirmationInput({
	onRecorded,
	value,
	clientName,
	shiftId,
	disabled,
	disclaimer,
}: {
	onRecorded: (result: VerbalConfirmationRecording) => void;
	value: VerbalConfirmationRecording | null;
	clientName: string;
	shiftId: number;
	disabled?: boolean;
	disclaimer?: string | null;
}) {
	const logger = useLogger();
	const [opened, setOpened] = useState(false);
	const [step, setStep] = useState<ModalStep>("prepare");
	const [elapsedSeconds, setElapsedSeconds] = useState(0);
	const [recordingError, setRecordingError] = useState<string | null>(null);
	const [ivrAlternativeAvailable, setIvrAlternativeAvailable] = useState(false);
	const [ivrInstructionsOpened, setIvrInstructionsOpened] = useState(false);
	const [pendingRecording, setPendingRecording] =
		useState<VerbalConfirmationRecording | null>(null);
	const [audioUrl, setAudioUrl] = useState<string | null>(null);
	const mediaRecorderRef = useRef<MediaRecorder | null>(null);
	const chunksRef = useRef<Blob[]>([]);
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const startTimeRef = useRef<number>(0);
	const audioMonitorRef = useRef<AudioLevelMonitor | null>(null);
	const playbackAudioRef = useRef<HTMLAudioElement | null>(null);
	const recordingStartRef = useRef<symbol | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [isRecordingStarting, setIsRecordingStarting] = useState(false);

	useEffect(() => {
		const recording = pendingRecording ?? value;
		if (!recording) {
			setAudioUrl(null);
			return;
		}

		const nextAudioUrl = URL.createObjectURL(recording.blob);
		setAudioUrl(nextAudioUrl);
		return () => URL.revokeObjectURL(nextAudioUrl);
	}, [pendingRecording, value]);

	useEffect(() => {
		const playbackAudio = playbackAudioRef.current;
		if (playbackAudio?.src === audioUrl) return;

		playbackAudio?.pause();
		playbackAudioRef.current = null;
		setIsPlaying(false);
	}, [audioUrl]);

	useEffect(() => {
		return () => {
			recordingStartRef.current = null;
			if (timerRef.current) clearInterval(timerRef.current);
			playbackAudioRef.current?.pause();
			if (mediaRecorderRef.current?.state === "recording") {
				mediaRecorderRef.current.stop();
			}
		};
	}, []);

	const closeFlow = useCallback(() => {
		if (step === "recording") return;
		recordingStartRef.current = null;
		setIsRecordingStarting(false);
		logger.info(
			`verbal_confirmation_flow_closed shiftId=${shiftId} step=${step}`,
		);
		setOpened(false);
		setPendingRecording(null);
		setRecordingError(null);
		setIvrAlternativeAvailable(false);
		setStep("prepare");
	}, [logger, shiftId, step]);

	const startFlow = useCallback(() => {
		logger.info(
			`verbal_confirmation_flow_opened shiftId=${shiftId} replacing=${value !== null}`,
		);
		playbackAudioRef.current?.pause();
		setRecordingError(null);
		setIvrAlternativeAvailable(false);
		setPendingRecording(null);
		setStep("prepare");
		setOpened(true);
	}, [logger, shiftId, value]);

	const togglePlayback = useCallback(() => {
		if (!audioUrl) return;

		let playbackAudio = playbackAudioRef.current;
		if (!playbackAudio) {
			playbackAudio = new Audio(audioUrl);
			playbackAudio.onplay = () => setIsPlaying(true);
			playbackAudio.onpause = () => setIsPlaying(false);
			playbackAudio.onended = () => setIsPlaying(false);
			playbackAudioRef.current = playbackAudio;
		}

		if (playbackAudio.paused) {
			void playbackAudio.play().catch(() => setIsPlaying(false));
		} else {
			playbackAudio.pause();
			playbackAudio.currentTime = 0;
		}
	}, [audioUrl]);

	const stopRecording = useCallback(() => {
		if (mediaRecorderRef.current?.state === "recording") {
			mediaRecorderRef.current.stop();
		}
	}, []);

	useEffect(() => {
		if (step === "recording" && elapsedSeconds >= MAX_DURATION_SECONDS) {
			stopRecording();
		}
	}, [step, elapsedSeconds, stopRecording]);

	const startRecording = useCallback(async () => {
		if (
			recordingStartRef.current ||
			mediaRecorderRef.current?.state === "recording"
		)
			return;

		const recordingStart = Symbol("recording-start");
		recordingStartRef.current = recordingStart;
		setIsRecordingStarting(true);
		logger.info(`verbal_confirmation_recording_requested shiftId=${shiftId}`);
		setRecordingError(null);
		setIvrAlternativeAvailable(false);
		let stream: MediaStream | null = null;
		let monitor: AudioLevelMonitor | null = null;
		let mediaRecorder: MediaRecorder | null = null;
		try {
			const mimeType =
				typeof MediaRecorder === "undefined"
					? null
					: MediaRecorder.isTypeSupported("audio/webm")
						? "audio/webm"
						: MediaRecorder.isTypeSupported("audio/mp4")
							? "audio/mp4"
							: null;
			if (!mimeType) {
				logger.warn(
					`verbal_confirmation_recording_unsupported shiftId=${shiftId}`,
				);
				recordingStartRef.current = null;
				setIsRecordingStarting(false);
				setRecordingError(UNSUPPORTED_RECORDING_ERROR);
				setIvrAlternativeAvailable(true);
				return;
			}

			stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			if (recordingStartRef.current !== recordingStart) {
				stopStream(stream);
				return;
			}

			monitor = createAudioLevelMonitor(stream);
			audioMonitorRef.current = monitor;
			if (monitor) monitor.start();

			mediaRecorder = new MediaRecorder(stream, { mimeType });
			mediaRecorderRef.current = mediaRecorder;
			const recordingMimeType = mediaRecorder.mimeType;
			chunksRef.current = [];

			mediaRecorder.ondataavailable = (event) => {
				if (event.data.size > 0) chunksRef.current.push(event.data);
			};

			mediaRecorder.onstop = () => {
				stopStream(stream);
				if (timerRef.current) {
					clearInterval(timerRef.current);
					timerRef.current = null;
				}

				const levelResult = monitor?.stop();
				if (audioMonitorRef.current === monitor) audioMonitorRef.current = null;
				const durationSeconds = Math.round(
					(Date.now() - startTimeRef.current) / 1000,
				);

				if (durationSeconds < MIN_DURATION_SECONDS) {
					logger.warn(
						`verbal_confirmation_recording_rejected shiftId=${shiftId} reason=too_short durationSeconds=${durationSeconds}`,
					);
					setRecordingError(
						`Record for at least ${MIN_DURATION_SECONDS} seconds before stopping.`,
					);
					setStep("prompt");
					return;
				}

				if (levelResult && isRecordingSilent(levelResult)) {
					logger.warn(
						`verbal_confirmation_recording_rejected shiftId=${shiftId} reason=silence durationSeconds=${durationSeconds} significantAudioRatio=${levelResult.significantAudioRatio.toFixed(3)} peakRms=${levelResult.peakRms.toFixed(3)} samples=${levelResult.totalSamples}`,
					);
					setRecordingError(
						"No speech was detected. Move the device closer and ask the client to speak clearly.",
					);
					setStep("prompt");
					return;
				}

				const blob = new Blob(chunksRef.current, {
					type: recordingMimeType,
				});
				logger.info(
					`verbal_confirmation_recording_ready shiftId=${shiftId} durationSeconds=${durationSeconds} sizeBytes=${blob.size} contentType=${blob.type} audioMonitoring=${levelResult !== undefined}`,
				);
				setPendingRecording({
					blob,
					durationSeconds,
				});
				setStep("review");
			};

			mediaRecorder.start(1000);
			recordingStartRef.current = null;
			setIsRecordingStarting(false);
			logger.info(
				`verbal_confirmation_recording_started shiftId=${shiftId} contentType=${mimeType} audioMonitoring=${monitor !== null}`,
			);
			startTimeRef.current = Date.now();
			setElapsedSeconds(0);
			setStep("recording");
			timerRef.current = setInterval(() => {
				setElapsedSeconds(
					Math.round((Date.now() - startTimeRef.current) / 1000),
				);
			}, 500);
		} catch (error) {
			if (mediaRecorder?.state === "recording") mediaRecorder.stop();
			else stopStream(stream);
			monitor?.stop();
			if (audioMonitorRef.current === monitor) audioMonitorRef.current = null;
			if (recordingStartRef.current !== recordingStart) return;

			recordingStartRef.current = null;
			setIsRecordingStarting(false);
			const errorName = error instanceof Error ? error.name : "UnknownError";
			const errorMessage =
				error instanceof Error ? error.message : String(error);
			if (error instanceof DOMException && error.name === "NotAllowedError") {
				logger.warn(
					`verbal_confirmation_microphone_denied shiftId=${shiftId} errorName=${errorName} error=${errorMessage}`,
				);
				setRecordingError(
					"Microphone access denied. Please enable microphone permissions in your device settings.",
				);
			} else {
				logger.error(
					`verbal_confirmation_microphone_failed shiftId=${shiftId} errorName=${errorName} error=${errorMessage}`,
				);
				setRecordingError(
					"Unable to access the microphone. You can check out by phone instead.",
				);
				setIvrAlternativeAvailable(true);
			}
		}
	}, [logger, shiftId]);

	const useRecording = useCallback(() => {
		if (!pendingRecording) return;
		logger.info(
			`verbal_confirmation_recording_accepted shiftId=${shiftId} durationSeconds=${pendingRecording.durationSeconds} sizeBytes=${pendingRecording.blob.size} contentType=${pendingRecording.blob.type}`,
		);
		onRecorded(pendingRecording);
		setOpened(false);
		setPendingRecording(null);
		setStep("prepare");
	}, [logger, onRecorded, pendingRecording, shiftId]);

	const formatTime = (seconds: number) => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins}:${secs.toString().padStart(2, "0")}`;
	};

	return (
		<>
			{value ? (
				<Stack gap="xs">
					<Group gap="sm">
						<ThemeIcon color="green" variant="light" size="lg">
							<IconCheck size={20} />
						</ThemeIcon>
						<Text size="sm" fw={500}>
							Voice confirmation ready ({formatTime(value.durationSeconds)})
						</Text>
					</Group>
					<Group grow>
						<Button variant="light" onClick={startFlow} disabled={disabled}>
							Replace recording
						</Button>
						{audioUrl && (
							<Button
								variant="subtle"
								leftSection={
									isPlaying ? (
										<IconPlayerStop size={16} />
									) : (
										<IconPlayerPlay size={16} />
									)
								}
								onClick={togglePlayback}
							>
								{isPlaying ? "Stop" : "Listen"}
							</Button>
						)}
					</Group>
				</Stack>
			) : (
				<Stack gap="xs">
					<Text size="sm" c="dimmed">
						Record the client confirming this visit.
					</Text>
					<Button
						leftSection={<IconMicrophone size={20} />}
						onClick={startFlow}
						disabled={disabled}
						size="lg"
						fullWidth
					>
						Start voice confirmation
					</Button>
				</Stack>
			)}

			<Modal
				opened={opened}
				onClose={closeFlow}
				title="Client voice confirmation"
				closeOnClickOutside={step !== "recording"}
				closeOnEscape={step !== "recording"}
				withCloseButton={step !== "recording"}
				closeButtonProps={{
					"aria-label": value
						? "Cancel re-recording"
						: "Cancel voice confirmation",
				}}
				fullScreen
				styles={{
					content: {
						display: "flex",
						paddingTop: "var(--mantine-spacing-xl)",
						paddingBottom:
							"calc(var(--mantine-spacing-xl) + var(--safe-area-inset-bottom))",
						flex: 1,
						flexDirection: "column",
					},
					body: {
						display: "flex",
						flex: 1,
						flexDirection: "column",
					},
				}}
			>
				<Stack gap="lg" style={{ flex: 1 }}>
					<Text size="xs" c="dimmed" fw={600} tt="uppercase">
						Step{" "}
						{step === "prepare"
							? 1
							: step === "prompt"
								? 2
								: step === "recording"
									? 3
									: 4}{" "}
						of 4
					</Text>

					{step === "prepare" && (
						<>
							<Stack align="center" gap="xs">
								<ThemeIcon size={64} radius="xl" color="blue" variant="light">
									<IconMicrophone size={32} />
								</ThemeIcon>
								<Text fw={600} size="xl">
									Prepare to record
								</Text>
							</Stack>
							<List spacing="md" size="sm" icon={<IconCheck size={16} />}>
								<List.Item>
									Move the device close to the client&apos;s mouth.
								</List.Item>
								<List.Item>Ask the client to speak clearly.</List.Item>
								<List.Item>
									The client, not the caregiver, must speak.
								</List.Item>
							</List>
							<Alert
								icon={<IconAlertTriangle size={18} />}
								color="orange"
								variant="light"
							>
								{disclaimer || caregiverAppVerbalConfirmationDisclaimer}
							</Alert>
							<Stack gap="xs" mt="auto">
								<Button size="lg" fullWidth onClick={() => setStep("prompt")}>
									I&apos;m with the client
								</Button>
							</Stack>
						</>
					)}

					{step === "prompt" && (
						<>
							<Text ta="center" fw={600} size="xl">
								Ask the client to say:
							</Text>
							<Paper withBorder p="lg" radius="md">
								<Text ta="center" fw={600} size="lg">
									My name is {clientName}. I confirm that the visit details are
									accurate.
								</Text>
							</Paper>
							{recordingError && (
								<Alert
									icon={<IconAlertTriangle size={18} />}
									color="red"
									variant="light"
								>
									{recordingError}
									{ivrAlternativeAvailable && (
										<Button
											mt="sm"
											variant="light"
											color="red"
											leftSection={<IconPhoneCall size={16} />}
											onClick={() => {
												setOpened(false);
												setPendingRecording(null);
												setRecordingError(null);
												setIvrAlternativeAvailable(false);
												setStep("prepare");
												setIvrInstructionsOpened(true);
											}}
										>
											View phone instructions
										</Button>
									)}
								</Alert>
							)}
							<Stack gap="xs" mt="auto">
								<Button
									size="lg"
									fullWidth
									leftSection={<IconMicrophone size={20} />}
									onClick={startRecording}
									disabled={isRecordingStarting}
								>
									Start recording
								</Button>
								{recordingError?.startsWith("Microphone access denied") &&
									import.meta.env.VITE_CAPACITOR_BUILD === "true" && (
										<Button
											variant="light"
											leftSection={<IconSettings size={16} />}
											onClick={async () => {
												const { NativeSettings, AndroidSettings, IOSSettings } =
													await import("capacitor-native-settings");
												await NativeSettings.open({
													optionAndroid: AndroidSettings.ApplicationDetails,
													optionIOS: IOSSettings.App,
												});
											}}
										>
											Open Settings
										</Button>
									)}
							</Stack>
						</>
					)}

					{step === "recording" && (
						<Stack align="center" gap="lg" py="md" style={{ flex: 1 }}>
							<ThemeIcon size={80} radius="xl" color="red" variant="light">
								<IconMicrophone size={40} />
							</ThemeIcon>
							<Text size="xl" fw={700}>
								{formatTime(elapsedSeconds)}
							</Text>
							<Text ta="center" size="lg">
								&quot;My name is {clientName}. I confirm that the visit details
								are accurate.&quot;
							</Text>
							<Text ta="center" size="sm">
								Keep recording for at least {MIN_DURATION_SECONDS} seconds, then
								stop when the client is finished.
							</Text>
							<Button
								mt="auto"
								size="lg"
								fullWidth
								color="red"
								leftSection={<IconPlayerStop size={20} />}
								onClick={stopRecording}
							>
								Stop recording
							</Button>
						</Stack>
					)}

					{step === "review" && pendingRecording && (
						<Stack align="center" gap="md" style={{ flex: 1 }}>
							<ThemeIcon size={64} radius="xl" color="green" variant="light">
								<IconCheck size={32} />
							</ThemeIcon>
							<Text fw={600} size="xl">
								Review recording
							</Text>
							<Text size="sm" ta="center">
								Confirm that the client is audible and said their full name and
								confirmation. By continuing, you attest that this is the
								client&apos;s authentic verbal confirmation of accurate visit
								details.
							</Text>
							{audioUrl && (
								// biome-ignore lint/a11y/useMediaCaption: client voice confirmations do not have captions.
								<audio controls src={audioUrl} />
							)}
							<Stack gap="xs" mt="auto" w="100%">
								<Button size="lg" fullWidth onClick={useRecording}>
									Use this recording
								</Button>
								<Button
									variant="subtle"
									fullWidth
									onClick={() => {
										logger.info(
											`verbal_confirmation_recording_retry shiftId=${shiftId}`,
										);
										setPendingRecording(null);
										setStep("prompt");
									}}
								>
									Record again
								</Button>
							</Stack>
						</Stack>
					)}
				</Stack>
			</Modal>
			<IvrInstructionsModal
				opened={ivrInstructionsOpened}
				onClose={() => setIvrInstructionsOpened(false)}
			/>
		</>
	);
}

function stopStream(stream: MediaStream | null) {
	if (!stream) return;
	for (const track of stream.getTracks()) track.stop();
}
